import { useCallback, useRef, useState, type FormEvent } from 'react';
import { flushSync } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';

export interface ResourceValues {
  cpu_available: number;
  memory_available_mb: number;
  storage_available_gb: number;
}

interface EndpointResourcesDialogProps {
  open: boolean;
  onClose: () => void;
  initial: ResourceValues;
  /** Rejecting keeps the dialog open and shows the error inline. */
  onSave: (values: ResourceValues) => Promise<void>;
}

// Limits mirror the server's validation (UpdateResources).
const MAX_CPU = 128;
const MAX_MEMORY_MB = 256000;
const MAX_STORAGE_GB = 10000;

function parseNumber(text: string, max: number, integer: boolean): number | null {
  const t = text.trim();
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n > max) return null;
  if (integer && !Number.isInteger(n)) return null;
  return n;
}

/** Shell stays mounted; the form is mounted only while open so each open starts from the current values. */
export function EndpointResourcesDialog(props: EndpointResourcesDialogProps) {
  const submittingRef = useRef(false);
  const { open, onClose } = props;
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submittingRef.current) onClose();
      }}
    >
      {open ? <ResourcesForm {...props} submittingRef={submittingRef} captureOpener={captureOpener} restoreFocus={restoreFocus} /> : null}
    </Dialog>
  );
}

type Field = 'cpu' | 'memory' | 'storage';

function ResourcesForm({
  initial,
  onSave,
  onClose,
  submittingRef,
  captureOpener,
  restoreFocus,
}: EndpointResourcesDialogProps & {
  submittingRef: { current: boolean };
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}) {
  const refs = {
    cpu: useRef<HTMLInputElement>(null),
    memory: useRef<HTMLInputElement>(null),
    storage: useRef<HTMLInputElement>(null),
  };
  const focusAlert = useCallback((el: HTMLParagraphElement | null) => {
    el?.focus();
  }, []);
  const [text, setText] = useState<Record<Field, string>>({
    cpu: String(initial.cpu_available),
    memory: String(initial.memory_available_mb),
    storage: String(initial.storage_available_gb),
  });
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorSeq, setErrorSeq] = useState(0);

  const parsed = {
    cpu: parseNumber(text.cpu, MAX_CPU, false),
    memory: parseNumber(text.memory, MAX_MEMORY_MB, true),
    storage: parseNumber(text.storage, MAX_STORAGE_GB, true),
  };
  const messages: Record<Field, string> = {
    cpu: `Enter a number, e.g. 0.5 (0 to ${MAX_CPU}).`,
    memory: `Enter a whole number (0 to ${MAX_MEMORY_MB.toLocaleString()}).`,
    storage: `Enter a whole number (0 to ${MAX_STORAGE_GB.toLocaleString()}).`,
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    flushSync(() => setAttempted(true));
    const { cpu, memory, storage } = parsed;
    if (cpu === null || memory === null || storage === null) {
      const first = (['cpu', 'memory', 'storage'] as const).find((f) => parsed[f] === null);
      if (first) refs[first].current?.focus();
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await onSave({ cpu_available: cpu, memory_available_mb: memory, storage_available_gb: storage });
      submittingRef.current = false;
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to save resources');
      setErrorSeq((n) => n + 1);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const block = (e: Event) => {
    if (submittingRef.current) e.preventDefault();
  };

  const field = (f: Field, label: string, unit: string) => {
    const invalid = attempted && parsed[f] === null;
    return (
      <div className="space-y-2">
        <Label htmlFor={`res-${f}`}>{label}</Label>
        <Input
          ref={refs[f]}
          id={`res-${f}`}
          inputMode="decimal"
          autoComplete="off"
          value={text[f]}
          onChange={(e) => setText((s) => ({ ...s, [f]: e.target.value }))}
          required
          aria-required="true"
          aria-invalid={invalid ? true : undefined}
          aria-describedby={invalid ? `res-${f}-error` : undefined}
          readOnly={submitting}
          className="tabular-nums"
        />
        <p className="text-xs text-muted-foreground">{unit}</p>
        {invalid ? (
          <p id={`res-${f}-error`} className="text-sm text-destructive">
            {messages[f]}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <DialogContent
      className="sm:max-w-md"
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        refs.cpu.current?.select();
        refs.cpu.current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
      onEscapeKeyDown={block}
      onPointerDownOutside={block}
      onInteractOutside={block}
    >
      <DialogHeader>
        <DialogTitle>Edit resource values</DialogTitle>
        <DialogDescription>
          These values are for reference only. Saving them pauses automatic resource sync for this endpoint, and apps are only placed on endpoints with synced host totals — use 'Reset to automatic' to resume.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void handleSubmit(e)} noValidate className="space-y-4">
        {field('cpu', 'CPU cores', 'Cores available')}
        {field('memory', 'Memory MB', 'Megabytes available')}
        {field('storage', 'Storage GB', 'Gigabytes available')}

        {error ? (
          <p key={errorSeq} ref={focusAlert} tabIndex={-1} role="alert" className="text-sm text-destructive outline-none">
            {error}
          </p>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            aria-disabled={submitting}
            onClick={() => {
              if (!submitting) onClose();
            }}
          >
            Cancel
          </Button>
          <Button type="submit" aria-disabled={submitting} aria-busy={submitting} className={submitting ? 'pointer-events-none opacity-70' : undefined}>
            {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
